import React, { useState, useEffect } from 'react';
import { Modal, Table, Checkbox, Select, Button, Space, Typography } from 'antd';
import { getRequest, ResponseDataType } from 'api';
import { toast } from 'react-toastify';
import type { ColumnsType } from 'antd/es/table';
import { ClassYearDataType, StudentExceptionItem, StudentDataType } from './PromotionModal';

const { Text } = Typography;
const { Option } = Select;

interface ExceptionModalProps {
    open: boolean;
    oldClass: ClassYearDataType;
    newClassYears: ClassYearDataType[];
    existingExceptions: StudentExceptionItem[];
    onClose: () => void;
    onSave: (newExceptions: StudentExceptionItem[]) => void;
}

export const ExceptionModal: React.FC<ExceptionModalProps> = ({ 
    open, oldClass, newClassYears, existingExceptions, onClose, onSave 
}) => {
    const [students, setStudents] = useState<StudentDataType[]>([]);
    const [loading, setLoading] = useState(false);
    
    // Local state to manage exceptions being edited in this modal
    const [localExceptions, setLocalExceptions] = useState<StudentExceptionItem[]>([]);

    useEffect(() => {
        if (open) {
            fetchStudents();
            // Initialize local exceptions with the ones passed from parent
            // But only keep those that belong to students in this class
            // Actually, we'll just keep all of them in a map for easy updates
            setLocalExceptions(existingExceptions);
        }
    }, [open]);

    const fetchStudents = async () => {
        setLoading(true);
        try {
            const res: ResponseDataType<{ list: StudentDataType[] }> = await getRequest(
                `/cms/student/get-students-by-class-year?classYearId=${oldClass.classYearId}&status=1`
            );
            if (res.code === 200 && res.info && Array.isArray(res.info.list)) {
                setStudents(res.info.list);
            } else {
                setStudents([]);
            }
        } catch (err) {
            toast.error('Lỗi khi tải danh sách học sinh');
            setStudents([]);
        } finally {
            setLoading(false);
        }
    };

    const getExceptionForStudent = (studentId: number): StudentExceptionItem | undefined => {
        return localExceptions.find(e => e.studentId === studentId);
    };

    const updateException = (studentId: number, updates: Partial<StudentExceptionItem>) => {
        setLocalExceptions(prev => {
            const existing = prev.find(e => e.studentId === studentId);
            if (existing) {
                // If turning off both retained and dropout, remove exception
                const newEx = { ...existing, ...updates };
                if (!newEx.retained && !newEx.dropOut) {
                    return prev.filter(e => e.studentId !== studentId);
                }
                return prev.map(e => e.studentId === studentId ? newEx : e);
            } else {
                // Add new exception
                const newEx: StudentExceptionItem = {
                    studentId,
                    newClassYearId: null,
                    retained: false,
                    dropOut: false,
                    ...updates
                };
                if (!newEx.retained && !newEx.dropOut) return prev; // Don't add empty
                return [...prev, newEx];
            }
        });
    };

    const columns: ColumnsType<StudentDataType> = [
        {
            title: 'Mã HS',
            dataIndex: 'studentCode',
            key: 'studentCode',
            width: '15%'
        },
        {
            title: 'Họ tên',
            dataIndex: 'name',
            key: 'name',
            width: '25%'
        },
        {
            title: 'Nghỉ học',
            key: 'dropOut',
            width: '15%',
            align: 'center',
            render: (_, record) => {
                const ex = getExceptionForStudent(record.studentId);
                return (
                    <Checkbox 
                        checked={ex?.dropOut || false}
                        onChange={(e) => updateException(record.studentId, { 
                            dropOut: e.target.checked, 
                            retained: e.target.checked ? false : ex?.retained // can't be both
                        })}
                    />
                );
            }
        },
        {
            title: 'Lưu ban',
            key: 'retained',
            width: '15%',
            align: 'center',
            render: (_, record) => {
                const ex = getExceptionForStudent(record.studentId);
                return (
                    <Checkbox 
                        checked={ex?.retained || false}
                        disabled={ex?.dropOut} // disabled if dropped out
                        onChange={(e) => updateException(record.studentId, { 
                            retained: e.target.checked,
                            // when retaining, if we don't have a new class, maybe default to old class name?
                        })}
                    />
                );
            }
        },
        {
            title: 'Xếp vào lớp (nếu Lưu ban)',
            key: 'newClass',
            width: '30%',
            render: (_, record) => {
                const ex = getExceptionForStudent(record.studentId);
                if (!ex?.retained) return <Text type="secondary">--</Text>;
                
                return (
                    <Select
                        showSearch
                        allowClear
                        placeholder="Chọn lớp"
                        style={{ width: '100%' }}
                        value={ex.newClassYearId}
                        onChange={(val) => updateException(record.studentId, { newClassYearId: val })}
                        filterOption={(input, option) =>
                            (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                        }
                        options={newClassYears.map(nc => ({
                            value: nc.classYearId,
                            label: `${nc.className} (Khối ${nc.classLevel})`
                        }))}
                    />
                );
            }
        }
    ];

    const handleSave = () => {
        // Return only exceptions that belong to students currently loaded in this modal?
        // Wait, localExceptions contains ALL existing exceptions + updates for this class.
        // We can just return localExceptions to the parent and the parent will overwrite completely.
        onSave(localExceptions);
    };

    return (
        <Modal
            title={`Cấu hình ngoại lệ: ${oldClass.className}`}
            open={open}
            onCancel={onClose}
            width={800}
            footer={[
                <Button key="cancel" onClick={onClose}>
                    Hủy
                </Button>,
                <Button key="save" type="primary" onClick={handleSave}>
                    Lưu cấu hình
                </Button>
            ]}
            destroyOnClose
            maskClosable={false}
        >
            <div style={{ marginBottom: 16 }}>
                <Text type="secondary">
                    Tick chọn những học sinh không theo luồng chuyển lớp mặc định. <br/>
                    - <b>Nghỉ học:</b> Hệ thống sẽ đóng dữ liệu học sinh này.<br/>
                    - <b>Lưu ban:</b> Chọn lớp đúp lại ở năm học tiếp theo.
                </Text>
            </div>
            
            <Table
                columns={columns}
                dataSource={students}
                rowKey="studentId"
                pagination={false}
                size="small"
                bordered
                loading={loading}
                scroll={{ y: 400 }}
            />
        </Modal>
    );
};
